import React, { useState, useRef } from 'react';
import {
	Card,
	Row,
	Typography,
	Space,
	Button,
	Divider,
	Input,
	Popover,
	message,
	InputNumber,
	Select,
} from 'antd';
import {
	CopyOutlined,
	UploadOutlined,
	ThunderboltOutlined,
	CloseOutlined,
	InfoCircleFilled,
	EditOutlined,
	CheckOutlined,
	CloseOutlined as CloseIcon,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { fetchWrapper } from '../../helpers';

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;

const ImageGeneratorCard = ({
	// Props for configuration
	initialMediaText = '',
	maxReferenceImages = 3,
	generateEndpoint = '/dist/generate-image',
	authToken = null,
	isPublished = false,
	postId = null,
	// Optional callbacks for external communication
	onMediaTextChange = null,
	onImageGenerated = null,
	onCopyMediaText = null,
}) => {
	const { t } = useTranslation();

	// State management - completely self-contained
	const [mediaText, setMediaText] = useState(initialMediaText);
	const [editMode, setEditMode] = useState(false);
	const [referenceImages, setReferenceImages] = useState([]);
	const [generating, setGenerating] = useState(false);
	const [generatedImageUrl, setGeneratedImageUrl] = useState(null);
	const [generatedImageBlob, setGeneratedImageBlob] = useState(null);
	const [generationInstruction, setGenerationInstruction] = useState('');
	const [imageStyle, setImageStyle] = useState('modern');

	// Refs
	const fileInputRef = useRef(null);
	const abortControllerRef = useRef(null);

	// Media text handlers
	const handleMediaTextChange = (e) => {
		const newText = e.target.value;
		setMediaText(newText);
		if (onMediaTextChange) {
			onMediaTextChange(newText);
		}
	};

	const handleCopyMediaText = () => {
		navigator.clipboard
			.writeText(mediaText || '')
			.then(() => {
				message.success(t('imageGenerator.copySuccess'));
				if (onCopyMediaText) {
					onCopyMediaText(mediaText);
				}
			})
			.catch(() => {
				message.error(t('imageGenerator.copyError'));
			});
	};

	const toggleEditMode = () => {
		setEditMode(!editMode);
	};

	// Reference image handlers
	const handleFileSelect = (event) => {
		const files = Array.from(event.target.files || []);
		if (!files.length) return;

		const validFiles = [];
		const errors = [];

		// Validate files
		files.forEach((file) => {
			if (!file.type.startsWith('image/')) {
				errors.push(t('imageGenerator.errors.notImageFile', { fileName: file.name }));
				return;
			}
			if (file.size > 8 * 1024 * 1024) {
				errors.push(t('imageGenerator.errors.fileTooLarge', { fileName: file.name }));
				return;
			}
			validFiles.push(file);
		});

		// Check total count
		const totalAfterAdd = referenceImages.length + validFiles.length;
		if (totalAfterAdd > maxReferenceImages) {
			errors.push(t('imageGenerator.errors.maxImagesExceeded', { max: maxReferenceImages }));
			validFiles.splice(maxReferenceImages - referenceImages.length);
		}

		// Show errors
		if (errors.length > 0) {
			message.error(errors.join(', '));
		}

		// Add valid files
		if (validFiles.length > 0) {
			setReferenceImages((prev) => [...prev, ...validFiles]);
		}

		// Reset input
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const handleRemoveReference = (index) => {
		setReferenceImages((prev) => prev.filter((_, i) => i !== index));
	};

	const handleClearReferences = () => {
		setReferenceImages([]);
	};

	// Convert URL to File object for drag and drop
	const fetchImageAsFile = async (imageUrl) => {
		try {
			const response = await fetch(imageUrl);
			const blob = await response.blob();

			const filename = `generated-image-${Date.now()}.png`;
			const file = new File([blob], filename, { type: blob.type || 'image/png' });

			return { blob, file };
		} catch (error) {
			console.error('Error fetching image as file:', error);
			return null;
		}
	};

	// Image generation
	const handleGenerateImage = async () => {
		if (referenceImages.length === 0) {
			message.error(t('imageGenerator.errors.noReferenceImages'));
			return;
		}

		setGenerating(true);
		setGeneratedImageUrl(null);
		setGeneratedImageBlob(null);

		// Setup abort controller for cancellation
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}
		const abortController = new AbortController();
		abortControllerRef.current = abortController;

		try {
			const formData = new FormData();

			// Append reference images
			referenceImages.forEach((file) => {
				formData.append('referenceImages', file);
			});

			// Add generation instruction for the AI
			const instruction =
				generationInstruction || t('imageGenerator.defaultInstruction', { style: imageStyle });

			formData.append('generation_instruction', instruction);

			// Add postId if available
			if (postId) {
				formData.append('postId', postId);
			}

			const response = await fetchWrapper.post(generateEndpoint, formData, {
				raw: true,
				signal: abortController.signal,
			});

			if (!response.ok) {
				const errorData = await response.json().catch(() => ({}));
				throw new Error(
					errorData.error ||
						t('imageGenerator.errors.generationFailed', {
							status: response.status,
							statusText: response.statusText,
						}),
				);
			}

			const result = await response.json();

			if (!result.imageUrl) {
				throw new Error(t('imageGenerator.errors.invalidResponse'));
			}

			setGeneratedImageUrl(result.imageUrl);

			// Fetch the image as a blob for dragging
			const imageData = await fetchImageAsFile(result.imageUrl);
			if (imageData) {
				setGeneratedImageBlob(imageData);
			}

			message.success(t('imageGenerator.generateSuccess'));

			if (onImageGenerated) {
				onImageGenerated(result.imageUrl);
			}
		} catch (error) {
			if (error.name === 'AbortError') {
				message.info(t('imageGenerator.generationCancelled'));
			} else {
				console.error('Generation error:', error);
				message.error(error.message || t('imageGenerator.errors.generationError'));
			}
		} finally {
			setGenerating(false);
			abortControllerRef.current = null;
		}
	};

	const cancelGeneration = () => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}
	};

	// Handle drag start for generated image
	const handleGeneratedImageDragStart = (e) => {
		if (!generatedImageBlob) {
			e.preventDefault();
			return;
		}

		const dragData = {
			type: 'generated-image',
			source: 'ai-image-generator',
			imageUrl: generatedImageUrl,
			fileName: generatedImageBlob.file.name,
			fileType: generatedImageBlob.file.type,
			fileSize: generatedImageBlob.file.size,
		};

		e.dataTransfer.setData('application/json', JSON.stringify(dragData));
		e.dataTransfer.setData('text/plain', JSON.stringify(dragData));
		e.dataTransfer.effectAllowed = 'copy';

		// Store the actual file in a temporary global variable
		if (!window.draggedFiles) window.draggedFiles = {};
		window.draggedFiles['generated-image'] = generatedImageBlob.file;
	};

	const handleGeneratedImageDragEnd = () => {
		// Clean up the temporary storage
		if (window.draggedFiles && window.draggedFiles['generated-image']) {
			delete window.draggedFiles['generated-image'];
		}
	};

	// Render edit button
	const renderEditButton = () => (
		<Button
			icon={editMode ? <CheckOutlined /> : <EditOutlined />}
			onClick={toggleEditMode}
			type={editMode ? 'primary' : 'default'}
		>
			{editMode ? t('imageGenerator.buttons.save') : t('imageGenerator.buttons.edit')}
		</Button>
	);

	// Style options for image generation
	const styleOptions = [
		{ value: 'modern', label: t('imageGenerator.styles.modern') },
		{ value: 'minimalist', label: t('imageGenerator.styles.minimalist') },
		{ value: 'realistic', label: t('imageGenerator.styles.realistic') },
		{ value: 'illustration', label: t('imageGenerator.styles.illustration') },
		{ value: 'professional', label: t('imageGenerator.styles.professional') },
	];

	return (
		<Card className="shadow-sm" style={{ marginTop: 16 }} bodyStyle={{ padding: 16 }}>
			{/* Header */}
			<Row justify="space-between" align="middle">
				<Title level={5} style={{ margin: 0 }}>
					{t('imageGenerator.title')}
				</Title>
				<Space>
					<Button icon={<CopyOutlined />} onClick={handleCopyMediaText} disabled={!mediaText}>
						{t('imageGenerator.buttons.copyText')}
					</Button>
					{renderEditButton()}
				</Space>
			</Row>

			<Divider style={{ margin: '12px 0' }} />

			{/* Media Text Area */}
			{editMode ? (
				<Input.TextArea
					value={mediaText}
					onChange={handleMediaTextChange}
					autoSize={{ minRows: 3, maxRows: 8 }}
					placeholder={t('imageGenerator.placeholders.mediaDescription')}
				/>
			) : (
				<Paragraph className="!mb-0" style={{ minHeight: '60px' }}>
					{mediaText || t('imageGenerator.noMediaDescription')}
				</Paragraph>
			)}

			{/* Reference Images & Generation Section */}
			<Divider style={{ margin: '16px 0' }} />

			<div className="text-center">
				{/* Generation Settings */}
				<div className="mb-4 text-left">
					<Text strong className="block mb-2">
						{t('imageGenerator.generationSettings')}
					</Text>
					<Space direction="vertical" className="w-full">
						<div>
							<Text className="block mb-1">{t('imageGenerator.imageStyle')}</Text>
							<Select
								value={imageStyle}
								onChange={setImageStyle}
								className="w-full"
								disabled={generating}
							>
								{styleOptions.map((style) => (
									<Option key={style.value} value={style.value}>
										{style.label}
									</Option>
								))}
							</Select>
						</div>
						<div>
							<Text className="block mb-1">{t('imageGenerator.additionalInstructions')}</Text>
							<Input.TextArea
								value={generationInstruction}
								onChange={(e) => setGenerationInstruction(e.target.value)}
								placeholder={t('imageGenerator.placeholders.instructions')}
								rows={2}
								disabled={generating}
							/>
						</div>
					</Space>
				</div>

				{/* Reference Images Display */}
				{referenceImages.length > 0 && (
					<div className="mb-4">
						<Row justify="space-between" align="middle" className="mb-2">
							<Text strong>
								{t('imageGenerator.referenceImages', {
									count: referenceImages.length,
									max: maxReferenceImages,
								})}
							</Text>
							<Button
								type="text"
								danger
								size="small"
								onClick={handleClearReferences}
								disabled={generating}
							>
								{t('imageGenerator.buttons.clearAll')}
							</Button>
						</Row>
						<div className="grid grid-cols-3 gap-2 max-w-md mx-auto">
							{referenceImages.map((refImg, idx) => (
								<div key={idx} className="relative group">
									<img
										src={URL.createObjectURL(refImg)}
										alt={t('imageGenerator.referenceAlt', { number: idx + 1 })}
										className="w-full h-24 object-cover rounded-lg border border-gray-300 transition-opacity group-hover:opacity-75"
									/>
									<Button
										type="primary"
										danger
										shape="circle"
										size="small"
										icon={<CloseOutlined />}
										className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
										onClick={() => handleRemoveReference(idx)}
										disabled={generating}
									/>
									<div className="absolute bottom-1 left-1 bg-black bg-opacity-60 text-white text-xs px-2 py-0.5 rounded">
										{idx + 1}
									</div>
								</div>
							))}
						</div>
					</div>
				)}

				{/* Action Buttons */}
				<Space direction="vertical" className="w-full">
					<Button
						icon={<UploadOutlined />}
						onClick={() => fileInputRef.current?.click()}
						disabled={generating || referenceImages.length >= maxReferenceImages}
					>
						{referenceImages.length > 0
							? t('imageGenerator.buttons.addMore')
							: t('imageGenerator.buttons.addReference')}
					</Button>

					<Button
						type="primary"
						icon={<ThunderboltOutlined />}
						onClick={handleGenerateImage}
						loading={generating}
						disabled={referenceImages.length === 0}
					>
						{t('imageGenerator.buttons.generateImage')}
					</Button>

					{generating && (
						<Button danger icon={<CloseIcon />} onClick={cancelGeneration}>
							{t('imageGenerator.buttons.cancel')}
						</Button>
					)}
				</Space>

				{/* Generated Image Result - NOW DRAGGABLE */}
				{generatedImageUrl && (
					<div className="mt-4 p-3 border rounded-lg">
						<Text strong className="block mb-2">
							{t('imageGenerator.generatedImageLabel')}
						</Text>
						<div
							draggable={!!generatedImageBlob}
							onDragStart={handleGeneratedImageDragStart}
							onDragEnd={handleGeneratedImageDragEnd}
							style={{
								cursor: generatedImageBlob ? 'grab' : 'default',
								position: 'relative',
								display: 'inline-block',
							}}
						>
							<img
								src={generatedImageUrl}
								alt={t('imageGenerator.generatedImageAlt')}
								className="w-full max-w-sm h-48 object-contain mx-auto border rounded"
								style={{ userSelect: 'none' }}
							/>
							{generatedImageBlob && (
								<div
									style={{
										position: 'absolute',
										top: 8,
										right: 8,
										background: 'rgba(0, 0, 0, 0.6)',
										color: 'white',
										padding: '4px 8px',
										borderRadius: 4,
										fontSize: 12,
										pointerEvents: 'none',
									}}
								>
									{t('imageGenerator.dragToAddHint')}
								</div>
							)}
						</div>
						<div className="mt-2 flex gap-2 justify-center">
							<Button size="small" onClick={() => navigator.clipboard.writeText(generatedImageUrl)}>
								{t('imageGenerator.buttons.copyUrl')}
							</Button>
							<a href={generatedImageUrl} target="_blank" rel="noopener noreferrer">
								<Button size="small" type="primary">
									{t('imageGenerator.buttons.open')}
								</Button>
							</a>
						</div>
					</div>
				)}

				{/* Hint for empty state */}
				{referenceImages.length === 0 && (
					<Text type="secondary" className="block mt-2 text-xs">
						{t('imageGenerator.emptyStateHint')}
					</Text>
				)}
			</div>

			{/* Hidden file input */}
			<input
				type="file"
				ref={fileInputRef}
				multiple
				accept="image/*"
				onChange={handleFileSelect}
				style={{ display: 'none' }}
			/>

			{/* Information Popover */}
			{!isPublished && (
				<Popover
					overlayStyle={{ maxWidth: 520 }}
					placement="bottom"
					content={
						<div style={{ fontSize: 12 }}>
							<p>
								<strong>{t('imageGenerator.guidelines.title')}</strong>
							</p>
							<ul>
								<li>{t('imageGenerator.guidelines.uploadTip')}</li>
								<li>{t('imageGenerator.guidelines.clarityTip')}</li>
								<li>{t('imageGenerator.guidelines.sizeTip')}</li>
								<li>{t('imageGenerator.guidelines.formatsTip')}</li>
								<li>{t('imageGenerator.guidelines.instructionsTip')}</li>
								<li>{t('imageGenerator.guidelines.dragTip')}</li>
							</ul>
						</div>
					}
					title={t('imageGenerator.guidelines.popoverTitle')}
				>
					<div className="flex justify-center gap-1 mt-4 items-center cursor-help">
						<InfoCircleFilled style={{ color: 'blueviolet' }} />
						<span>{t('imageGenerator.guidelines.linkText')}</span>
					</div>
				</Popover>
			)}
		</Card>
	);
};

export default ImageGeneratorCard;
